import { CameraControls } from '@react-three/drei';
import cameraControlsLib from 'camera-controls';
import * as THREE from "three";
import LayerMappingsEnum from "../../../enums/LayerMappingsEnum";
import { IMaterialSet } from "../../../services/MaterialManager";
import { CreateMaterialSet } from "../../../services/MaterialSetFactory";

import ViewerCameraTypeEnum from "../../../enums/ViewerCameraTypeEnum";
import { stringToEnum } from "../../../utils/commonUtils";
import ICanvasInputListener from "../../../logic/ICanvasInputListener";

enum NavigationModeEnum {
    Free,
    AlongThePlane
}

enum InputModeEnum {
    NotDefined,
    ApartmentSelect2d,
    FreeRoam3d
}

// NOTE: When adding viewer modes, remember to add them to initialViewerState.ViewerModes too
enum ViewerModeEnum {
    NotDefined = "not_defined", // Initial state
    ApartmentSelect2d = "apartment_select", // ApartmentSelect2d
    /* ApartmentRoam = "apartment_roam",
    ApartmentDimensioning = "apartment_dimensioning", */
    FreeRoam3d = "free_roam_3d",
    Dollhouse = "dollhouse",
    Marketing = "marketing"
}

export enum StageTypeEnum {
    Stage2d,
    Stage3d
}

class ViewerMode
{
    constructor(    modeEnum: ViewerModeEnum,
                    cameraType: ViewerCameraTypeEnum,
                    navigationMode: NavigationModeEnum,
                    inputMode: InputModeEnum,
                    stageType: StageTypeEnum,
                    layers: LayerMappingsEnum[],
                    materialSet: IMaterialSet,
                    useKeyboardControls: boolean = true) {
        this.modeEnum = modeEnum;
        this.cameraType = cameraType;
        this.navigationMode = navigationMode;
        this.inputMode = inputMode;
        this.stageType = stageType;
        this.layers = layers;
        this.materialSet = materialSet;
        this.useKeyboardControls = useKeyboardControls;
    }

    modeEnum: ViewerModeEnum;
    cameraType: ViewerCameraTypeEnum;
    navigationMode: NavigationModeEnum;
    inputMode: InputModeEnum;
    stageType: StageTypeEnum;

    layers: LayerMappingsEnum[];
    materialSet: IMaterialSet;
    useKeyboardControls: boolean;
}

const viewerModeFactory = (mode: ViewerModeEnum): ViewerMode => {

    switch (mode) {

        case ViewerModeEnum.NotDefined:
            return new ViewerMode(  ViewerModeEnum.NotDefined,
                                    ViewerCameraTypeEnum.Orthographic,
                                    NavigationModeEnum.AlongThePlane,
                                    InputModeEnum.NotDefined,
                                    StageTypeEnum.Stage2d,
                                    [],
                                    CreateMaterialSet(ViewerModeEnum.NotDefined),
                                    false);

        case ViewerModeEnum.ApartmentSelect2d:
            return new ViewerMode(  ViewerModeEnum.ApartmentSelect2d,
                                    ViewerCameraTypeEnum.Orthographic,
                                    NavigationModeEnum.AlongThePlane,
                                    InputModeEnum.ApartmentSelect2d,
                                    StageTypeEnum.Stage2d,
                                    [   LayerMappingsEnum.Stage, LayerMappingsEnum.Elements2d, /* LayerMappingsEnum.Spaces2d, */
                                        LayerMappingsEnum.DoorSymbols, LayerMappingsEnum.WindowSymbols, LayerMappingsEnum.Fixtures2d,
                                        LayerMappingsEnum.FloorCoverings2d, LayerMappingsEnum.WallCoverings2d, LayerMappingsEnum.Skirtings2d,
                                        LayerMappingsEnum.RoomNameLabels, LayerMappingsEnum.StoreyNames2d,
                                        LayerMappingsEnum.Annotations, LayerMappingsEnum.Annotations2d, LayerMappingsEnum.ReferenceLine,
                                        /* LayerMappingsEnum.DebugEntities */ ],
                                    CreateMaterialSet(ViewerModeEnum.ApartmentSelect2d),
                                    false);

        case ViewerModeEnum.FreeRoam3d:
            return new ViewerMode(  ViewerModeEnum.FreeRoam3d,
                                    ViewerCameraTypeEnum.Perspective,
                                    NavigationModeEnum.Free,
                                    InputModeEnum.FreeRoam3d,
                                    StageTypeEnum.Stage3d,
                                    [   LayerMappingsEnum.Stage, LayerMappingsEnum.Elements3d, /* LayerMappingsEnum.Spaces2d, */
                                        LayerMappingsEnum.DoorSymbols, LayerMappingsEnum.Fixtures3d,
                                        LayerMappingsEnum.FloorCoverings2d, LayerMappingsEnum.WallCoverings3dNoTexture, LayerMappingsEnum.Skirtings3d,
                                        LayerMappingsEnum.Annotations, LayerMappingsEnum.Annotations3d, LayerMappingsEnum.CeilingCoverings2d,
                                        LayerMappingsEnum.ReferenceLine,
                                        /* LayerMappingsEnum.DebugEntities */
                                         /* , LayerMappingsEnum.CeilingCoverings */ ],
                                    CreateMaterialSet(ViewerModeEnum.FreeRoam3d));

        case ViewerModeEnum.Dollhouse:
            return new ViewerMode(  ViewerModeEnum.Dollhouse,
                                    ViewerCameraTypeEnum.Perspective,
                                    NavigationModeEnum.Free,
                                    InputModeEnum.FreeRoam3d,
                                    StageTypeEnum.Stage3d,
                                    [   LayerMappingsEnum.Stage, LayerMappingsEnum.Elements3d, /* LayerMappingsEnum.Spaces2d, */
                                        LayerMappingsEnum.DoorSymbols, LayerMappingsEnum.Fixtures3d,
                                        LayerMappingsEnum.FloorCoverings3d, LayerMappingsEnum.WallCoverings3d, LayerMappingsEnum.Skirtings3d,
                                        LayerMappingsEnum.Annotations, LayerMappingsEnum.Annotations3d, LayerMappingsEnum.CeilingCoverings2d,
                                        LayerMappingsEnum.ReferenceLine,
                                        /* LayerMappingsEnum.DebugEntities */
                                         /* , LayerMappingsEnum.CeilingCoverings */ ],
                                    CreateMaterialSet(ViewerModeEnum.FreeRoam3d));

        case ViewerModeEnum.Marketing:
            return new ViewerMode(  ViewerModeEnum.Marketing,
                                    ViewerCameraTypeEnum.Perspective,
                                    NavigationModeEnum.AlongThePlane,
                                    InputModeEnum.ApartmentSelect2d,
                                    StageTypeEnum.Stage2d,
                                    [   LayerMappingsEnum.Stage, LayerMappingsEnum.Elements2d, LayerMappingsEnum.DoorSymbols, LayerMappingsEnum.WindowSymbols, 
                                        LayerMappingsEnum.Fixtures2d, LayerMappingsEnum.DebugEntities, LayerMappingsEnum.FloorCoverings2d,
                                        LayerMappingsEnum.RoomNameLabels],
                                    CreateMaterialSet(ViewerModeEnum.ApartmentSelect2d));

        default:
            throw new Error("ViewerMode not implemented");
    };
};

// TODO: Move this to be property of ViewerMode
const setControlsByViewerMode = (controls: CameraControls, mode: ViewerModeEnum, inputListener?: ICanvasInputListener) => {
    
    //controls.dampingFactor = 0.05;         // default ~0.05–0.1; lower = snappier response
    //controls.draggingDampingFactor = 0.1;

    switch (mode) {
        case ViewerModeEnum.ApartmentSelect2d:
            controls.mouseButtons.left = cameraControlsLib.ACTION.SCREEN_PAN;
            controls.mouseButtons.right = cameraControlsLib.ACTION.NONE;
            controls.mouseButtons.middle = cameraControlsLib.ACTION.NONE;
            
            controls.mouseButtons.wheel = cameraControlsLib.ACTION.ZOOM;

            controls.touches.one = cameraControlsLib.ACTION.TOUCH_SCREEN_PAN;
            controls.touches.two = cameraControlsLib.ACTION.TOUCH_ZOOM;

            controls.minPolarAngle=0.0; 
            controls.maxPolarAngle=Math.PI;
            
            controls.dollySpeed = 5;
            controls.truckSpeed = 1;
            inputListener?.setTouchZoomDollySpeed(10);

            break;
        case ViewerModeEnum.FreeRoam3d:
        case ViewerModeEnum.Dollhouse:
        default:
            controls.mouseButtons.left = cameraControlsLib.ACTION.TRUCK;
            controls.mouseButtons.right = cameraControlsLib.ACTION.ROTATE;
            controls.mouseButtons.middle = cameraControlsLib.ACTION.OFFSET;
            
            controls.mouseButtons.wheel = cameraControlsLib.ACTION.DOLLY;
            
            controls.touches.one = cameraControlsLib.ACTION.TOUCH_TRUCK;
            controls.touches.two = cameraControlsLib.ACTION.TOUCH_DOLLY_ROTATE;
//            controls.touches.two = cameraControlsLib.ACTION.NONE;

            controls.minPolarAngle=0.0; 
            controls.maxPolarAngle=Math.PI/2;
            controls.dollySpeed = 1.5;
            controls.truckSpeed = 2.2;
            inputListener?.setTouchZoomDollySpeed(1.5);

            break;
        /* default:
            console.error("ViewerMode not implemented"); */
    }
}

const createViewerModeByName = (mode: string): ViewerMode => {
    let modeEnum = stringToEnum(ViewerModeEnum, mode);

    if (modeEnum === undefined) {
        modeEnum = ViewerModeEnum.ApartmentSelect2d;
        console.error(`ViewerMode ${mode} not found. Defaulting to ${modeEnum}`);
    }

    return viewerModeFactory(modeEnum);
    
};

export {ViewerModeEnum, NavigationModeEnum, ViewerMode, viewerModeFactory, createViewerModeByName, setControlsByViewerMode};
